"use client";
import { useState, useRef, useEffect } from "react";
import { Fab, Grow, Stack, Typography } from "@mui/material";
import { ArrowUpward as ScrollUpArrow } from "@mui/icons-material";

import { observer, useStore } from "../../../service/mobx";
import { entityMap } from "../../../component/Search/Hooks";
import { PaperCardSize5 } from "../../../component/Card/Paper";
import { CardTaskSize4 } from "../../../component/Card/Task";
import { CardVideoSize3 } from "../../../component/Card/External/Video";

function Top100({ searchParams: { type } }) {
  const [page, setPage] = useState(1);
  const { search, utilities } = useStore();
  const cards = {
    tag: [CardTaskSize4, "surface.color"],
    video: [CardVideoSize3, "surface.color"],
    paper: [PaperCardSize5, "surface.container.low"]
  };
  const { entityName, label } = entityMap[type] ?? {};
  const [Card = () => null, tone] = cards?.[entityName] ?? [];
  const count = search.count[label];
  const displayedItems = search.results.slice(0, 10 * page);
  const loading =
    search.loadingResults ||
    (search.filterStars !== undefined && search.loadingStars);

  return (
    <>
      <Typography paragraph variant="titleSm" color="surface.on.color">
        {loading
          ? "Loading..."
          : count
          ? `Showing ${search.results.length}${
              search.loadingCount ? "" : ` of ${utilities.formatNumber(count)}`
            }`
          : undefined}
      </Typography>
      {search.results.length === 0 && loading === false ? (
        <Typography typography={{ compact: "titleMd", expanded: "headlineSm" }}>
          {`No results for ${
            search.userInput ? `"${search.userInput}"` : "these filters"
          }. Try again`}
        </Typography>
      ) : (
        <Stack useFlexGap spacing={3}>
          {displayedItems.map(result => (
            <Card key={result.id} tone={tone} {...{ [entityName]: result }} />
          ))}
          {loading || search.results.length === displayedItems.length ? null : (
            <LoadNextPage setPage={setPage} />
          )}
          <ScrollBackToTop />
        </Stack>
      )}
    </>
  );
}

export default observer(Top100);

function LoadNextPage({ setPage }) {
  const observerRef = useRef();

  useEffect(() => {
    if (observerRef.current) {
      const element = observerRef.current;
      const observer = new IntersectionObserver(
        ([{ isIntersecting }]) => {
          // If the observed entry is intersecting, increment the page number
          if (isIntersecting) {
            setPage(prevPage => prevPage + 1);
          }
        },
        { threshold: 0.5 } // Adjust threshold as needed
      );

      observer.observe(element);

      return () => observer.unobserve(element);
    }
  }, [setPage]);

  return (
    <Typography ref={observerRef} variant="bodyLg">
      Loading more items...
    </Typography>
  );
}
function ScrollBackToTop() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () =>
      setIsVisible(window.scrollY > window.innerHeight);

    window.addEventListener("scroll", toggleVisibility);

    return () => {
      window.removeEventListener("scroll", toggleVisibility);
    };
  }, []);

  return (
    <Grow mountOnEnter unmountOnExit in={isVisible}>
      <Fab
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        sx={theme => ({
          position: "fixed",
          bottom: 24,
          right: 24,
          width: { compact: 56, large: 96 },
          height: { compact: 56, large: 96 },
          bgcolor: "primary.container",
          boxShadow: theme.elevation[3],
          borderRadius: {
            compact: theme.shape.lg.round,
            large: theme.shape.xl.round
          },
          transition: theme.transitions.create("all"),
          "&:hover": {
            bgcolor: "primary.container",
            boxShadow: theme.elevation[4],
            backgroundImage: `linear-gradient(${theme.palette.alpha(
              theme.palette.primary.on.container,
              0.08
            )}, ${theme.palette.alpha(
              theme.palette.primary.on.container,
              0.08
            )}) !important`
          }
        })}
      >
        <ScrollUpArrow
          sx={{
            color: "primary.on.container",
            width: { compact: 24, large: 36 },
            height: { compact: 24, large: 36 }
          }}
        />
      </Fab>
    </Grow>
  );
}
